import React, { useMemo, useState } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import authService from "../services/authService";
import { showToast } from "../utils/showToast";
import "../styles/dashboardTheme.css";

const createMathChallenge = () => ({
  a: Math.floor(Math.random() * 10) + 1,
  b: Math.floor(Math.random() * 10) + 1,
});

const useRecoveryToken = () => {
  const { token: tokenFromPath } = useParams();
  const location = useLocation();
  return useMemo(() => {
    if (tokenFromPath) return tokenFromPath;
    const searchToken = new URLSearchParams(location.search).get("token");
    return searchToken || "";
  }, [location.search, tokenFromPath]);
};

export default function ResetPassword() {
  const recoveryToken = useRecoveryToken();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mathAnswer, setMathAnswer] = useState("");
  const [challenge, setChallenge] = useState(createMathChallenge);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const correctAnswer = challenge.a + challenge.b;
  const regenerateChallenge = () => {
    setChallenge(createMathChallenge());
    setMathAnswer("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword || !mathAnswer) {
      showToast({ type: "warning", message: "Todos los campos son obligatorios" });
      return;
    }
    if (password.length < 6) {
      showToast({ type: "warning", message: "La contraseña debe tener al menos 6 caracteres" });
      return;
    }
    if (password !== confirmPassword) {
      showToast({ type: "danger", message: "Las contraseñas no coinciden" });
      return;
    }
    if (Number(mathAnswer) !== correctAnswer) {
      showToast({ type: "danger", message: "Respuesta incorrecta al ejercicio" });
      return;
    }
    if (!recoveryToken) {
      showToast({ type: "danger", message: "Token de recuperación inválido o faltante" });
      return;
    }

    setLoading(true);

    try {
      const data = await authService.resetPassword({ token: recoveryToken, newPassword: password });
      showToast({ type: "success", message: data.message || "Contraseña actualizada correctamente" });
      setPassword("");
      setConfirmPassword("");
      regenerateChallenge();
      setTimeout(() => {
        navigate("/"); // va al login
      }, 2000);
    } catch (error) {
      const msg = error?.data?.message || error.message || "No se pudo actualizar la contraseña";
      showToast({ type: "danger", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="text-center mb-4">
          <span className="dashboard-eyebrow d-inline-block mb-1">Seguridad</span>
          <h2 className="auth-title mb-2">Restablecer contraseña</h2>
          <p className="auth-subtitle">Crea una nueva contraseña y confirma tu identidad</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="password" className="form-label dashboard-label">Nueva contraseña</label>
            <input
              type="password"
              id="password"
              className="form-control auth-input"
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="confirmPassword" className="form-label dashboard-label">Confirmar contraseña</label>
            <input
              type="password"
              id="confirmPassword"
              className="form-control auth-input"
              placeholder="********"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label className="form-label dashboard-label">Verificación rápida</label>
            <div className="auth-challenge">
              <span className="fw-semibold">
                {challenge.a} + {challenge.b} =
              </span>
              <input
                type="number"
                className="form-control auth-input"
                style={{ maxWidth: "120px" }}
                value={mathAnswer}
                onChange={(e) => setMathAnswer(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="btn btn-sm dashboard-btn-primary text-white"
                style={{ width: "auto" }}
                onClick={regenerateChallenge}
                disabled={loading}
              >
                Cambiar
              </button>
            </div>
          </div>

          <button type="submit" className="btn dashboard-btn-primary w-100" disabled={loading}>
            {loading ? "Actualizando..." : "Actualizar contraseña"}
          </button>

          <div className="auth-extra">
            <Link to="/" className="auth-link">Volver al inicio de sesión</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
